import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Optimized hook for Home page - fetches only necessary data
 * Implements parallel queries and proper caching per Engineering Playbook
 */

export interface HomeProduct {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  image: string | null;
  price: number | null;
  msrp: number | null;
  price_duration: string | null;
  rating: number | null;
  review_count: number | null;
  categories: any;
  is_hot: boolean;
  is_select: boolean;
}

export interface HomeProductWithId extends HomeProduct {
  id: string;
}

export interface HomeData {
  hotProducts: HomeProductWithId[];
  featuredProducts: HomeProductWithId[];
  categories: any[];
  profile: { full_name: string } | null;
  favorites: { product_id: string }[];
}

export const useHomeProducts = (userId?: string) => {
  return useQuery({
    queryKey: ['home-data', userId],
    queryFn: async (): Promise<HomeData> => {
      if (userId) {
        // Authenticated user - fetch all data in parallel
        const [hotProductsRes, featuredProductsRes, categoriesRes, profileRes, favoritesRes] = await Promise.all([
          supabase
            .from('products')
            .select('id, slug, title, subtitle, image, price, msrp, price_duration, rating, review_count, categories, is_hot, is_select')
            .eq('is_published', true)
            .eq('is_hot', true)
            .order('sort_order', { ascending: true })
            .limit(20),
          
          supabase
            .from('products')
            .select('id, slug, title, subtitle, image, price, msrp, price_duration, rating, review_count, categories, is_hot, is_select')
            .eq('is_published', true)
            .order('sort_order', { ascending: true })
            .limit(6),
          
          supabase
            .from('categories')
            .select('*')
            .order('sort_order', { ascending: true }),
          
          supabase
            .from('profiles')
            .select('full_name')
            .eq('user_id', userId)
            .single(),
          
          supabase
            .from('user_favorites')
            .select('product_id')
            .eq('user_id', userId)
        ]);
        
        return {
          hotProducts: (hotProductsRes.data || []) as HomeProduct[],
          featuredProducts: (featuredProductsRes.data || []) as HomeProduct[],
          categories: categoriesRes.data || [],
          profile: profileRes.data,
          favorites: favoritesRes.data || [],
        };
      } else {
        // Guest user - fetch only public data in parallel
        const [hotProductsRes, featuredProductsRes, categoriesRes] = await Promise.all([
          supabase
            .from('products')
            .select('id, slug, title, subtitle, image, price, msrp, price_duration, rating, review_count, categories, is_hot, is_select')
            .eq('is_published', true)
            .eq('is_hot', true)
            .order('sort_order', { ascending: true })
            .limit(20),
          
          supabase
            .from('products')
            .select('id, slug, title, subtitle, image, price, msrp, price_duration, rating, review_count, categories, is_hot, is_select')
            .eq('is_published', true)
            .order('sort_order', { ascending: true })
            .limit(6),
          
          supabase
            .from('categories')
            .select('*')
            .order('sort_order', { ascending: true }),
        ]);
        
        return {
          hotProducts: (hotProductsRes.data || []) as HomeProduct[],
          featuredProducts: (featuredProductsRes.data || []) as HomeProduct[],
          categories: categoriesRes.data || [],
          profile: null,
          favorites: [],
        };
      }
    },
    staleTime: 5 * 60 * 1000,      // Keep fresh for 5 min
    gcTime: 30 * 60 * 1000,        // Keep in cache for 30 min (formerly cacheTime)
    refetchOnMount: false,          // Don't refetch if data is fresh
    refetchOnWindowFocus: false,    // Don't refetch on tab focus
  });
};
